import { VStack, HStack, Box, Pressable, IconButton, useTheme } from 'native-base';
import { padStart } from 'lodash';
import moment from 'moment';
import MaterialIcons from 'react-native-vector-icons/MaterialIcons';

import { Text } from '@pimm/base';

type TimePickerProps = {
  _container?: React.ComponentProps<typeof Box>;
  defaultValue?: Date;
  isDisabled?: boolean;
  isPMDisabled?: boolean;
  isError?: boolean;
  /** Optional lower bound — only hours/minutes used. Disables down-arrows at boundary. */
  minTime?: Date;
  /** Optional upper bound — only hours/minutes used. Disables up-arrows at boundary. */
  maxTime?: Date;
  value?: Date;
  onChange?: (value: Date) => void;
};

export const TimePicker = ({
  _container,
  defaultValue,
  isDisabled,
  isPMDisabled = false,
  isError = false,
  minTime,
  maxTime,
  value,
  ...props
}: TimePickerProps) => {
  const { colors } = useTheme();
  const timeValue = value ?? defaultValue;
  const inputDate = moment(timeValue).format('YYYY-MM-DD');
  const inputTime = moment(timeValue).format('HH:mm');
  const hours = Number(inputTime?.slice(0, 2));
  const minutes = Number(inputTime.slice(3, 5));
  const meridiem = hours >= 12 && hours < 24 ? 'PM' : 'AM';

  // Boundary limits in total minutes (Infinity = unconstrained)
  const lo = minTime ? minTime.getHours() * 60 + minTime.getMinutes() : -Infinity;
  const hi = maxTime ? maxTime.getHours() * 60 + maxTime.getMinutes() : Infinity;
  const cur = hours * 60 + minutes;

  const hourUpDisabled = isDisabled || cur + 60 > hi;
  const hourDnDisabled = isDisabled || cur - 60 < lo;
  const minUpDisabled  = isDisabled || cur + 5  > hi;
  const minDnDisabled  = isDisabled || cur - 5  < lo;

  const arrowColor = (disabled: boolean) => (disabled ? colors.gray[400] : colors.gray[800]);

  const handleChangeTime = (hrs: number, mins: number) => {
    if (props.onChange) {
      const nextDate = moment(inputDate).set({ h: hrs, m: mins, s: 0 }).toDate();
      props.onChange(nextDate);
    }
  };

  const handleChangeHours = (x: number) => () => {
    let adjustedHours = hours + x;
    if (!minTime && !maxTime) {
      // Unconstrained: wrap around
      if (adjustedHours > 23) adjustedHours = 0;
      else if (adjustedHours < 0) adjustedHours = 23;
    }
    handleChangeTime(adjustedHours, minutes);
  };

  const handleChangeMinutes = (increment: boolean) => () => {
    let mins = minutes;
    if (increment) {
      mins = !maxTime && mins >= 55 ? 0 : mins + 5;
    } else {
      mins = !minTime && mins === 0 ? 55 : mins - 5;
    }
    handleChangeTime(Number(hours), mins);
  };

  const handleChangeMeridiem = (m: string) => {
    if (meridiem !== m) {
      if (m === 'PM' && isPMDisabled) return;
      const hrs = m === 'AM' ? -12 : 12;
      handleChangeTime(Number(hours) + hrs, Number(minutes));
    }
  };

  return (
    <Box bg="gray.100" borderColor="gray.200" borderWidth={1} borderRadius="lg" px={2.5} py={3} {..._container}>
      <HStack alignItems="center" justifyContent="center" space={2}>
        {/* Hours */}
        <VStack space={1.5} alignItems="center">
          <IconButton
            isDisabled={hourUpDisabled}
            width={7}
            height={7}
            variant="unstyled"
            p={0}
            icon={<MaterialIcons name="arrow-drop-up" size={30} color={arrowColor(hourUpDisabled)} />}
            onPress={handleChangeHours(1)}
          />
          <Text size="4xl" color={isDisabled ? 'gray.400' : 'black'} fontWeight={700}>
            {padStart(`${hours % 12 || 12}`, 2, '0') ?? '00'}
          </Text>
          <IconButton
            isDisabled={hourDnDisabled}
            width={7}
            height={7}
            variant="unstyled"
            p={0}
            icon={<MaterialIcons name="arrow-drop-down" size={30} color={arrowColor(hourDnDisabled)} />}
            onPress={handleChangeHours(-1)}
          />
        </VStack>

        <Box mb={1}>
          <Text size="4xl" color={isDisabled ? 'gray.400' : 'gray.900'} fontWeight={700}>
            :
          </Text>
        </Box>

        {/* Minutes */}
        <VStack space={1.5} alignItems="center">
          <IconButton
            isDisabled={minUpDisabled}
            width={7}
            height={7}
            variant="unstyled"
            p={0}
            icon={<MaterialIcons name="arrow-drop-up" size={30} color={arrowColor(minUpDisabled)} />}
            onPress={handleChangeMinutes(true)}
          />
          <Text size="4xl" color={isDisabled ? 'gray.400' : 'black'} fontWeight={700}>
            {padStart(`${minutes}`, 2, '0') ?? '00'}
          </Text>
          <IconButton
            isDisabled={minDnDisabled}
            width={7}
            height={7}
            variant="unstyled"
            p={0}
            icon={<MaterialIcons name="arrow-drop-down" size={30} color={arrowColor(minDnDisabled)} />}
            onPress={handleChangeMinutes(false)}
          />
        </VStack>

        {/* Meridiem */}
        <VStack space={2} ml={3}>
          {['AM', 'PM'].map(_meridiem => {
            const isButtonDisabled = isDisabled || (_meridiem === 'PM' && isPMDisabled);
            return (
            <Pressable
              key={_meridiem}
              isDisabled={isButtonDisabled}
              onPress={() => handleChangeMeridiem(_meridiem)}
              px={2.5}
              py={1}
              rounded="md"
              borderWidth={1}
              borderColor={isButtonDisabled && meridiem !== _meridiem ? 'gray.100' : meridiem === _meridiem ? 'yellow.400' : 'gray.200'}
              bg={isButtonDisabled && meridiem !== _meridiem ? 'gray.50' : meridiem === _meridiem ? 'yellow.400' : 'white'}
            >
              <Text size="md" fontWeight={600} color={isButtonDisabled ? 'gray.300' : 'gray.800'}>
                {_meridiem}
              </Text>
            </Pressable>
            );
          })}
        </VStack>
      </HStack>
    </Box>
  );
};

export default TimePicker;
